// ─── Lazy-Loaded Supabase Client ────────────────────────────────────────────
import type { SupabaseClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL ?? '';
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY ?? '';

// ─── Singleton Instance ──────────────────────────────────────────────────────
let _client: SupabaseClient | null = null;
let _loadingPromise: Promise<SupabaseClient> | null = null;

// ─── Connection Health State ─────────────────────────────────────────────────
// Tracks whether Supabase is reachable. Starts as null (unknown), then becomes
// true/false after the first health probe. All features degrade gracefully when false.
let _isHealthy: boolean | null = null;
let _healthCheckPromise: Promise<boolean> | null = null;
const _healthListeners = new Set<(healthy: boolean) => void>();

/**
 * Returns the singleton Supabase client, lazy-loading the library on first call.
 * ⚠️ DEFERRED LOADING: Only call this when you actually need Supabase (admin pages, auth-required features).
 * Public pages should NOT call this during initial render.
 */
export const getSupabase = async () => {
  if (_client) return _client;
  if (_loadingPromise) return _loadingPromise;

  _loadingPromise = (async () => {
    // ── Dynamic import — defers loading until actually needed ──
    const { createClient } = await import(/* @vite-ignore */ '@supabase/supabase-js');

    _client = createClient(
      supabaseUrl || 'https://placeholder.supabase.co',
      supabaseAnonKey || 'placeholder',
      {
        auth: {
          persistSession: true,
          autoRefreshToken: true,
          detectSessionInUrl: false,
        },
        // ⚠️ Realtime fully disabled — prevents WebSocket DNS lookup entirely
        realtime: {
          params: { 
            eventsPerSecond: 0,
          },
        },
        db: { 
          schema: 'public',
        },
        global: {
          headers: { 'x-client-info': 'rockhound-auto' },
          fetch: (url, options = {}) => {
            // Intercept and block any realtime WebSocket connection attempts
            if (url.toString().includes('/realtime/')) {
              return Promise.reject(new Error('Realtime connections disabled'));
            }
            return fetch(url, options);
          },
        },
      }
    );

    // Completely disable realtime channels after client creation
    if (_client.realtime) {
      // Override the channel method to prevent any subscription attempts
      const originalChannel = _client.realtime.channel.bind(_client.realtime);
      _client.realtime.channel = (...args: any[]) => {
        const channel = originalChannel(...args);
        // Return a neutered channel that never connects
        channel.subscribe = () => channel;
        return channel;
      };
      
      // Disconnect any existing connections
      _client.realtime.disconnect();
    }

    return _client;
  })();

  return _loadingPromise;
};

/**
 * Synchronous getter — only use after getSupabase() has already resolved.
 */
export function getSupabaseSync(): SupabaseClient {
  if (!_client) {
    throw new Error(
      'Supabase client not initialized. Call getSupabase() first and await the result.'
    );
  }
  return _client;
}

/**
 * Preload the Supabase client without waiting for the result.
 * ⚠️ DO NOT call this on public pages — only use for admin/auth-required routes.
 */
export function preloadSupabase(): void {
  if (!_client && !_loadingPromise) {
    getSupabase().catch(() => {
      // Silently ignore preload failures — the health check will surface status
    });
  }
}

// ─── Connection Health Check ─────────────────────────────────────────────────

/**
 * Probes Supabase availability with a lightweight REST ping.
 * - Runs once and caches the result for the session.
 * - Never throws — always resolves to true (healthy) or false (unavailable).
 * - Notifies all registered listeners when health status is determined.
 */
export async function checkSupabaseHealth(): Promise<boolean> {
  // Return cached result immediately
  if (_isHealthy !== null) return _isHealthy;

  // Deduplicate concurrent calls
  if (_healthCheckPromise) return _healthCheckPromise;

  _healthCheckPromise = (async (): Promise<boolean> => {
    try {
      if (!supabaseUrl || supabaseUrl.includes('placeholder')) {
        _isHealthy = false;
        return false;
      }

      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 5000);

      // Lightweight ping — just check if the REST endpoint responds
      const res = await fetch(`${supabaseUrl}/rest/v1/`, {
        method: 'HEAD',
        headers: { apikey: supabaseAnonKey },
        signal: controller.signal,
      });

      clearTimeout(timeout);
      _isHealthy = res.ok || res.status === 401; // 401 = reachable but needs auth
    } catch {
      // Network error, DNS failure, timeout — all treated as unavailable
      _isHealthy = false;
    }

    // Notify all listeners
    _healthListeners.forEach((fn) => {
      try { fn(_isHealthy as boolean); } catch { /* ignore */ }
    });

    _healthCheckPromise = null;
    return _isHealthy as boolean;
  })();

  return _healthCheckPromise;
}

/**
 * Subscribe to health status changes.
 * Callback fires once when health is determined, then on any subsequent change.
 * Returns an unsubscribe function.
 */
export function onSupabaseHealthChange(callback: (healthy: boolean) => void): () => void {
  _healthListeners.add(callback);
  // If health is already known, fire immediately
  if (_isHealthy !== null) {
    try { callback(_isHealthy); } catch { /* ignore */ }
  }
  return () => { _healthListeners.delete(callback); };
}

/**
 * Returns the last known health status without triggering a new check.
 * null = not yet checked, true = healthy, false = unavailable.
 */
export function getSupabaseHealthStatus(): boolean | null {
  return _isHealthy;
}

/**
 * Global security violation handler
 * Detects RLS policy violations and sends alerts to the security monitoring system
 */
export const handleSecurityViolation = async (error: any, context: {
  table: string;
  operation: string;
  attempted_by?: string;
}) => {
  const isRLSViolation = 
    error?.code === 'PGRST301' ||
    error?.code === '42501' ||
    error?.message?.includes('row-level security') ||
    error?.message?.includes('permission denied') ||
    error?.status === 403 ||
    error?.status === 401;

  if (!isRLSViolation) {
    return;
  }

  try {
    const ip = 'browser';

    const securityEvent = {
      table: context.table,
      operation: context.operation,
      attempted_by: context.attempted_by || 'anonymous',
      ip,
      timestamp: new Date().toISOString(),
      user_agent: navigator.userAgent,
      error_message: error?.message || 'RLS policy violation',
    };

    const supabase = await getSupabase();
    const functionUrl = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/security-alert`;
    
    await fetch(functionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(securityEvent),
    });

    if (import.meta.env.DEV) {
      console.warn('🚨 Security violation detected and reported:', context);
    }
  } catch (alertError) {
    if (import.meta.env.DEV) {
      console.error('Failed to send security alert:', alertError);
    }
  }
};